import { useEffect, useState } from "react";
import { Users, ArrowRight, MessageCircle, Sparkles, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatBRL } from "../../lib/api";
import SpaceGallery from "../../components/SpaceGallery";

const WHATSAPP_NUMBER = "5521975968405";

const TYPE_LABEL = {
  sala_reuniao: "Sala de Reunião",
  sala_privativa: "Sala Privativa",
  auditorio: "Auditório",
  mesa: "Mesa",
  estacao: "Estação",
  consultorio: "Consultório",
  escritorio_executivo: "Escritório Executivo",
};

export default function Espacos() {
  const [spaces, setSpaces] = useState([]);
  const [shared, setShared] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/public/spaces").then(({ data }) => setSpaces(data)),
      api.get("/public/shared-services").then(({ data }) => setShared(data)),
    ]).catch(() => {});
  }, []);

  return (
    <div data-testid="espacos-page" className="pb-24">
      <section className="pt-24 pb-12 bg-[#0A1128]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Nossos Espaços</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-tight">
            Ambientes desenhados
            <span className="block italic text-[#D4AF37] mt-2">para inspirar produtividade.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/60 mt-6 max-w-2xl mx-auto">
            5 salas privativas, consultório, salas de reunião para 5 e 8 pessoas, coworking e toda
            a infraestrutura compartilhada já inclusa.
          </p>
        </div>
      </section>

      {/* Serviços compartilhados */}
      {shared.length > 0 && (
        <section className="bg-[#0A1128] pb-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="gold-divider mb-8" />
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-[#D4AF37]" />
              <h2 className="font-display text-xl sm:text-2xl text-white">Serviços inclusos em todos os planos</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="shared-services-list">
              {shared.map((s) => (
                <div key={s.id || s.name} className="card-marketing px-4 py-3 text-white/80 text-sm">
                  {s.name}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Espaços */}
      <section className="bg-[#0A1128] pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Banner explicativo categoria x pacote */}
          <div className="mb-8 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 flex items-start gap-3" data-testid="espacos-category-banner">
            <Info size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-white/80 leading-relaxed">
              <strong className="text-[#D4AF37]">Como usar seus pacotes:</strong> cada espaço pertence a uma categoria.
              Ao comprar um pacote (64h ou 96h) da categoria correspondente, suas horas são
              <em> debitadas automaticamente</em> nas reservas, sem cobranças adicionais.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {spaces.map((s) => {
            const waMsg = `Olá! Quero reservar o espaço "${s.name}" no VM Corporate Piabetá.`;
            const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMsg)}`;
            return (
              <div key={s.id} className="card-marketing overflow-hidden" data-testid={`space-card-${s.id}`}>
                <SpaceGallery photos={s.photos || []} videoUrl={s.video_url} name={s.name} />
                <div className="p-6 sm:p-7">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]/70 mb-2">{s.category || TYPE_LABEL[s.type] || s.type}</div>
                  <div className="font-display text-xl sm:text-2xl text-white mb-2">{s.name}</div>
                  <p className="text-sm text-white/60 mb-5">{s.description}</p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-white/60 mb-5">
                    <div className="flex items-center gap-1.5"><Users size={14} className="text-[#D4AF37]" /> Capacidade: {s.capacity}</div>
                    {s.hourly_rate > 0 && <div>{formatBRL(s.hourly_rate)}/hora</div>}
                    {s.half_day_rate > 0 && <div>{formatBRL(s.half_day_rate)}/meia diária (4h)</div>}
                    {s.daily_rate > 0 && <div>{formatBRL(s.daily_rate)}/dia</div>}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {(s.resources || []).slice(0, 6).map((r) => (
                      <span key={r} className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/70">{r}</span>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <a
                      href={waHref}
                      target="_blank" rel="noreferrer"
                      className="btn-gold inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm flex-1"
                      data-testid={`space-whatsapp-${s.id}`}
                    >
                      <MessageCircle size={14} /> WhatsApp
                    </a>
                    <Link
                      to={`/espacos/${s.id}`}
                      className="btn-ghost-gold inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm flex-1"
                      data-testid={`reserve-${s.id}`}
                    >
                      Ver disponibilidade <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </section>
    </div>
  );
}
